import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DragEndEvent } from "@dnd-kit/core";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import CustomSelect from "src/app/components/CustomSelect/CustomSelect";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { translateMessage } from "src/app/methods/translateMessage";
import { VerticalDragAndDropList } from "src/app/components/VerticalDragAndDropList/VerticalDragAndDropList";
import { RootState } from "src/redux/reducers";
import DraggableAttributeListItem from "../components/DraggableAttributeListItem";
import {
  FieldOption,
  HandleAddFieldsProps,
  HandleRemoveFieldsProps,
  HandleUpdateTemplateValueParams,
  Template,
} from "../types";
import {
  handleUpdateProjectTemplate,
  mapFieldTypeToFieldName,
  rearrangeElementsInArray,
} from "../utils";

interface TemplateManagerAttributesTabProps {
  editedTemplate: Template;
  handleUpdateTemplateValue: (params: HandleUpdateTemplateValueParams) => void;
  handleAddFields: (params: HandleAddFieldsProps) => void;
  handleRemoveFields: (params: HandleRemoveFieldsProps) => void;
  setEditedTemplate: Dispatch<SetStateAction<Template | null>>;
  syncLocalTemplateWithServer: (uuid: string) => Promise<void>;
}

function TemplateManagerAttributesTab({
  editedTemplate,
  setEditedTemplate,
  handleUpdateTemplateValue,
  handleAddFields,
  handleRemoveFields,
  syncLocalTemplateWithServer,
}: TemplateManagerAttributesTabProps) {
  const [selectedFields, setSelectedFields] = useState<Array<FieldOption>>([]);

  const intl = useIntl();
  const dispatch = useDispatch();

  const {
    mainReducer: { activeWorkspaceUuid },
    settingsReducer: { globalProjectMetaFields },
  } = useSelector((state: RootState) => state);

  const fieldType = "project";
  const taskType = "null";

  const fieldsFromTemplate = useMemo(
    () => editedTemplate.globalProjectMetaFieldsUuids[taskType] ?? [],
    [editedTemplate],
  );

  const handleAddNewAttributes = (attributes: FieldOption[]) => {
    setSelectedFields(attributes);

    const newAttributes = attributes.filter(
      (attr) =>
        fieldsFromTemplate.findIndex((field) => field.uuid === attr.value) ===
        -1,
    );

    if (newAttributes.length === 0) {
      const removedFieldsIds = fieldsFromTemplate
        .filter(
          (field) =>
            attributes.findIndex((attr) => attr.value === field.uuid) === -1,
        )
        .map((field) => field.uuid);

      handleRemoveFields({
        fieldsToRemove: removedFieldsIds,
        fieldType,
        taskType,
      });
    } else {
      const mapNewAttributesToFieldData = newAttributes.map((attr) => ({
        uuid: attr.value,
        showInCampaignCreator: false,
        defaultValue: null,
        valueRequired: false,
      }));

      handleAddFields({
        newFieldsData: mapNewAttributesToFieldData,
        fieldType,
        taskType,
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = selectedFields.findIndex(
      (field) => field.value === active.id,
    );
    const newIndex = selectedFields.findIndex(
      (field) => field.value === over.id,
    );

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update the visual order of selectedFields
    const rearrangedSelectedFields = rearrangeElementsInArray(
      selectedFields,
      oldIndex,
      newIndex,
    );
    setSelectedFields(rearrangedSelectedFields);

    setEditedTemplate((prevTemplate) => {
      if (!prevTemplate) return null;

      const updatedTemplate = { ...prevTemplate };

      const fields =
        updatedTemplate[mapFieldTypeToFieldName[fieldType]][taskType];

      if (fields) {
        const rearrangedFields = rearrangeElementsInArray(
          fields,
          oldIndex,
          newIndex,
        );

        updatedTemplate[mapFieldTypeToFieldName[fieldType]][taskType] =
          rearrangedFields;
      }

      handleUpdateProjectTemplate({
        data: {
          wsWorkspaceUuid: activeWorkspaceUuid,
          ...updatedTemplate,
        },
        catchCallback: () => syncLocalTemplateWithServer(editedTemplate.uuid),
        dispatch,
      });

      return updatedTemplate;
    });
  };

  const fieldOptions = useMemo(
    () =>
      globalProjectMetaFields
        .filter((field) => field.taskType === null)
        .map((filteredField) => {
          return {
            label: filteredField.name,
            value: filteredField.uuid,
          };
        }),
    [globalProjectMetaFields],
  );

  useEffect(() => {
    const alreadySelectedFields: FieldOption[] = [];

    fieldsFromTemplate.forEach((fieldFromTemplate) => {
      const foundFieldOption = fieldOptions.find(
        (fieldOption) => fieldOption.value === fieldFromTemplate.uuid,
      );

      if (foundFieldOption) {
        alreadySelectedFields.push(foundFieldOption);
      }
    });

    setSelectedFields(alreadySelectedFields);
  }, [fieldOptions, fieldsFromTemplate]);

  return (
    <>
      <div className="template-manager__headers grid--attributes">
        <div>
          <IDHFormattedMessage id="ws_attribute" defaultMessage="Attribute" />
        </div>
        <div>
          <IDHFormattedMessage
            id="ws_campaign_creator"
            defaultMessage="Campaign Creator"
          />{" "}
        </div>
        <div>
          <IDHFormattedMessage
            id="ws_required_value"
            defaultMessage="Required Value"
          />
        </div>
        <div>
          <IDHFormattedMessage
            id="ws_default_value"
            defaultMessage="Default Value"
          />
        </div>
      </div>
      <div className="template-manager__selector">
        <CustomSelect
          options={fieldOptions}
          value={selectedFields}
          closeMenuOnSelect={false}
          isClearable={false}
          isMulti
          placeholder={translateMessage({
            intl,
            id: "ws_select_attribute",
            defaultMessage: "Select attribute",
          })}
          onChange={handleAddNewAttributes}
        />
      </div>
      <VerticalDragAndDropList.DndContext onDragEnd={handleDragEnd}>
        <VerticalDragAndDropList.SortableContext
          items={selectedFields.map((field) => field.value)}
        >
          <ul className="template-manager__list template-manager__list--attributes">
            {selectedFields.map((option: FieldOption) => {
              const foundGlobalField = globalProjectMetaFields.find(
                (field) => field.uuid === option.value,
              );
              const foundFieldFromTemplate = fieldsFromTemplate.find(
                (field) => field.uuid === option.value,
              );

              if (!foundGlobalField || !foundFieldFromTemplate) return null;

              return (
                <DraggableAttributeListItem
                  key={option.value}
                  option={option}
                  globalField={foundGlobalField}
                  templateField={foundFieldFromTemplate}
                  handleUpdateTemplateValue={handleUpdateTemplateValue}
                  handleRemoveFields={handleRemoveFields}
                />
              );
            })}
          </ul>
        </VerticalDragAndDropList.SortableContext>
      </VerticalDragAndDropList.DndContext>
    </>
  );
}

export default TemplateManagerAttributesTab;
